import * as z from 'zod'
import { Car } from '~/shared/api'
import { DebtStatusesEnum, DebtSourceTypesEnum } from '~/shared/config/enums'
import { dateSchema, priceRequiredScheme } from '~/shared/lib/schemas'
import { ApiModel, ToOneRelation } from './core'

const attributesSchema = z.object({
  balance: priceRequiredScheme,
  paid: priceRequiredScheme,
  paymentCost: priceRequiredScheme,
  remainingAmount: priceRequiredScheme,
  sourceExtId: z.string().optional(),
  sourceType: z.string(),
  sourceTypeLabel: z.string(),
  status: z.nativeEnum(DebtStatusesEnum),
  total: priceRequiredScheme,
  startedAt: dateSchema,
  deferredCommission: z.coerce.number().finite().nonnegative().nullish(),
  commissionDueAt: z.string().datetime({ offset: true }).nullish(),
  commissionStatus: z.enum(['PENDING', 'APPLIED', 'WAIVED']).nullish(),
})

const schema = z
  .object({
    createdAt: dateSchema,
    updatedAt: dateSchema,
  })
  .merge(attributesSchema)

export type DebtAttributes = z.infer<typeof schema>
export type ReportData = {
  debt: number
  paid: number
  total: number
}

export class Debt extends ApiModel<typeof schema, DebtAttributes> {
  static jsonApiType = 'debts'

  static schema = schema

  getSourceType(): DebtSourceTypesEnum {
    return this.getAttribute('sourceType')
  }
  getStatus(): DebtStatusesEnum {
    return this.getAttribute('status')
  }
  getStartAt(): string {
    return this.getAttribute('startAt')
  }
  getCreateAt(): string {
    return this.getAttribute('createdAt')
  }
  getTotal(): number {
    return this.getAttribute('total')
  }
  getPaid(): number {
    return this.getAttribute('paid')
  }
  getBalance(): number {
    return this.getAttribute('balance')
  }

  car(): ToOneRelation<Car, this> {
    return this.hasOne(Car)
  }
  getCar(): Car {
    return this.getRelation('car')
  }

  getSourceTitle(): string {
    const sourceType = this.getSourceType()
    return [
      DebtSourceTypesEnum.INSPECTION_VIOLATIONS,
      DebtSourceTypesEnum.INSPECTION_DAMAGES,
    ].includes(sourceType)
      ? `Осмотр`
      : [DebtSourceTypesEnum.EXTERNAL_PENALTY].includes(sourceType)
      ? `Штраф`
      : [DebtSourceTypesEnum.PLAN_INSPECTION_DELAY].includes(sourceType)
      ? `Просрочка`
      : [DebtSourceTypesEnum.TOLL_ROAD].includes(sourceType)
      ? 'Проезд'
      : [DebtSourceTypesEnum.OTHER_CHARGES].includes(sourceType)
      ? 'Задолженность по прочим начислениям'
      : ''
  }

  static async report(): Promise<ReportData> {
    const url = `${Debt.getJsonApiUrl()}/report`
    const res = await Debt.effectiveHttpClient.get(url)
    return res.getData()
  }

  static async repay(id: UniqueId, amount: number): Promise<void> {
    const url = `${Debt.getJsonApiUrl()}/${id}/repay`
    await Debt.effectiveHttpClient.post(url, { amount })
  }
}
